import { Pagination } from "@/components/briefing/pagination";
import type { PromptItem } from "@/lib/api/types";
import { paginate } from "@/lib/paginate";
import {
  FUNNEL_LABEL,
  FUNNEL_STAGES,
  type Filters,
  SCORE_FILTER_LABEL,
  STATUS_LABEL,
  type Sort,
  type SortKey,
  WEB_FILTER_LABEL,
  NO_FILTERS,
  NO_TAG,
  activeFilterCount,
  scoreTone,
  webLabel,
} from "@/lib/prompt-module";

type Props = {
  items: PromptItem[];
  selectedId: number | null;
  onSelect: (id: number) => void;
  checked: ReadonlySet<number>;
  /** `range`: Umschalt war gedrückt, alle Prompts bis zum letzten Klick mitnehmen. */
  onToggle: (id: number, range: boolean) => void;
  onToggleAll: (ids: number[], on: boolean) => void;
  page: number;
  onPage: (page: number) => void;
  sort: Sort;
  onSort: (key: SortKey) => void;
  filters: Filters;
  onFilters: (filters: Filters) => void;
  /** Prompts der Ansicht vor dem Filtern, für „x von y“ und den Hinweis bei leerem Ergebnis. */
  total: number;
  /** Alle Tags des Topics mit Zahl, für den Filter „Cluster“. */
  tags: { tag: string; count: number }[];
};

const FILTER = "h-11 w-full min-w-0 rounded-md border border-field bg-white px-2 text-[13px] font-normal text-ink";

/** Spaltenüberschrift als Knopf: Richtung steht als Pfeil und in `aria-sort`, nicht nur über Farbe. */
function SortHead({
  label,
  field,
  sort,
  onSort,
  className = "",
}: {
  label: string;
  field: SortKey;
  sort: Sort;
  onSort: (key: SortKey) => void;
  className?: string;
}) {
  const on = sort?.key === field;
  const dir = on ? sort.dir : null;
  return (
    <th
      scope="col"
      aria-sort={dir === "asc" ? "ascending" : dir === "desc" ? "descending" : "none"}
      className={`px-1 py-1 font-medium ${className}`}
    >
      <button
        type="button"
        onClick={() => onSort(field)}
        aria-label={`Nach ${label} sortieren${dir ? (dir === "asc" ? ", aufsteigend" : ", absteigend") : ""}`}
        className={`inline-flex min-h-11 items-center gap-1 rounded px-2 ${on ? "text-ink" : ""}`}
      >
        {label}
        <span aria-hidden className="font-mono text-[11px]">
          {dir === "asc" ? "▲" : dir === "desc" ? "▼" : "↕"}
        </span>
      </button>
    </th>
  );
}

/** Prompt-Liste (Mockup): Prompt, Funnel, Typ, Websuche, Score. Der Prompt ist der Knopf zum Auswählen. */
export function PromptTable({ items, selectedId, onSelect, checked, onToggle, onToggleAll, page, onPage, sort, onSort, filters, onFilters, total, tags }: Props) {
  const filtered = activeFilterCount(filters) > 0;
  const set = <K extends keyof Filters>(key: K, value: Filters[K]) => onFilters({ ...filters, [key]: value });
  const view = paginate(items, page);
  const ids = view.items.map((item) => item.id);
  const all = ids.length > 0 && ids.every((id) => checked.has(id));
  return (
    <div className="flex min-w-0 flex-col">
      <div className="overflow-x-auto">
        <table aria-label="Prompts" className="w-full min-w-[760px] border-collapse text-sm">
          <thead>
            <tr className="text-left text-xs text-muted">
              <th className="w-11 px-3 py-2.5">
                <input
                  type="checkbox"
                  checked={all}
                  disabled={ids.length === 0}
                  onChange={(event) => onToggleAll(ids, event.target.checked)}
                  aria-label="Alle Prompts auf dieser Seite auswählen"
                  className="size-5 accent-accent"
                />
              </th>
              <SortHead label="Prompt" field="text" sort={sort} onSort={onSort} />
              <SortHead label="Cluster" field="tag" sort={sort} onSort={onSort} />
              <SortHead label="Funnel" field="funnel" sort={sort} onSort={onSort} />
              <SortHead label="Typ" field="kind" sort={sort} onSort={onSort} />
              <SortHead label="Websuche" field="web" sort={sort} onSort={onSort} className="whitespace-nowrap" />
              <SortHead label="Score" field="score" sort={sort} onSort={onSort} className="text-right" />
            </tr>
            <tr className="border-t border-neutral-bg align-top">
              <td />
              <td className="px-3 pb-2">
                <input
                  type="search"
                  value={filters.text}
                  onChange={(event) => set("text", event.target.value)}
                  placeholder="Text enthält …"
                  aria-label="Prompts nach Text filtern"
                  className={FILTER}
                />
              </td>
              <td className="px-3 pb-2">
                <select
                  value={filters.tag}
                  onChange={(event) => set("tag", event.target.value)}
                  aria-label="Nach Cluster filtern"
                  className={FILTER}
                >
                  <option value="">Alle</option>
                  {tags.map((entry) => (
                    <option key={entry.tag} value={entry.tag}>
                      {entry.tag} ({entry.count})
                    </option>
                  ))}
                  <option value={NO_TAG}>Ohne Cluster</option>
                </select>
              </td>
              <td className="px-3 pb-2">
                <select
                  value={filters.funnel}
                  onChange={(event) => set("funnel", event.target.value as Filters["funnel"])}
                  aria-label="Nach Funnel filtern"
                  className={FILTER}
                >
                  <option value="">Alle</option>
                  {FUNNEL_STAGES.map((stage) => (
                    <option key={stage} value={stage}>
                      {FUNNEL_LABEL[stage]}
                    </option>
                  ))}
                  <option value="none">Ohne Phase</option>
                </select>
              </td>
              <td className="px-3 pb-2">
                <select
                  value={filters.kind}
                  onChange={(event) => set("kind", event.target.value as Filters["kind"])}
                  aria-label="Nach Typ filtern"
                  className={FILTER}
                >
                  <option value="">Alle</option>
                  <option value="branded">branded</option>
                  <option value="unbranded">unbranded</option>
                  <option value="none">Ohne Typ</option>
                </select>
              </td>
              <td className="px-3 pb-2">
                <select
                  value={filters.web}
                  onChange={(event) => set("web", event.target.value as Filters["web"])}
                  aria-label="Nach Websuche filtern"
                  className={FILTER}
                >
                  <option value="">Alle</option>
                  {Object.entries(WEB_FILTER_LABEL).map(([key, label]) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  ))}
                </select>
              </td>
              <td className="px-3 pb-2">
                <select
                  value={filters.score}
                  onChange={(event) => set("score", event.target.value as Filters["score"])}
                  aria-label="Nach Score filtern"
                  className={FILTER}
                >
                  <option value="">Alle</option>
                  {Object.entries(SCORE_FILTER_LABEL).map(([key, label]) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  ))}
                </select>
              </td>
            </tr>
          </thead>
          <tbody>
            {view.items.map((item) => {
              const on = item.id === selectedId;
              const tone = scoreTone(item.score);
              return (
                <tr
                  key={item.id}
                  className={`border-t border-neutral-bg align-top ${on ? "bg-[#eef3fa] shadow-[inset_3px_0_0_#1f5fad]" : "bg-white"}`}
                >
                  <td className="px-3 py-2.5">
                    <input
                      type="checkbox"
                      checked={checked.has(item.id)}
                      readOnly
                      onClick={(event) => onToggle(item.id, event.shiftKey)}
                      aria-label={`Auswählen: ${item.text}`}
                      className="size-5 accent-accent"
                    />
                  </td>
                  <td className="px-3 py-1.5">
                    <button
                      type="button"
                      onClick={() => onSelect(item.id)}
                      aria-current={on ? "true" : undefined}
                      className="flex min-h-11 w-full flex-col items-start justify-center gap-1 text-left"
                    >
                      <span className="leading-snug">{item.text}</span>
                      {item.status !== "vorschlag" && (
                        <span className="rounded bg-neutral-bg px-2 py-0.5 text-xs font-medium text-[#3a424a]">
                          {STATUS_LABEL[item.status]}
                        </span>
                      )}
                    </button>
                  </td>
                  <td className="px-3 py-2.5 text-[13px]">
                    {item.tag ? <span className="inline-block rounded bg-neutral-bg px-2 py-0.5 text-xs font-medium text-[#3a424a]">{item.tag}</span> : "–"}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2.5 text-[13px]">{item.funnel ? FUNNEL_LABEL[item.funnel] : "–"}</td>
                  <td className="whitespace-nowrap px-3 py-2.5 text-[13px]">{item.kind ?? "–"}</td>
                  <td className="whitespace-nowrap px-3 py-2.5 font-mono text-[13px]">{webLabel(item.web)}</td>
                  <td className="px-3 py-2.5 text-right">
                    <span
                      aria-label={item.score === null ? "Score offen" : `Score ${item.score}, ${tone.label}`}
                      className={`inline-block rounded px-2 py-0.5 font-mono text-[13px] font-semibold ${tone.className}`}
                    >
                      {item.score ?? "–"}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {filtered && (
        <p role="status" className="flex flex-wrap items-center gap-3 px-5 py-2 text-[13px] text-muted">
          {items.length} von {total} Prompts passen zu den Filtern.
          <button
            type="button"
            onClick={() => onFilters(NO_FILTERS)}
            className="min-h-11 rounded-md border border-field bg-white px-3 text-[13px] text-ink"
          >
            Filter zurücksetzen
          </button>
        </p>
      )}
      {items.length === 0 && (
        <p className="px-5 py-6 text-sm text-muted">{total === 0 ? "Keine Prompts in dieser Ansicht." : "Kein Prompt passt zu den Filtern."}</p>
      )}
      <Pagination page={view} label="Prompts" onPage={onPage} />
    </div>
  );
}
